import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import { AdminStatus, IAdmin } from "@/interfaces/admin.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

// here Sup stands for query for super/admin api while WS for workspace

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["admin", "single-admin"],
  endpoints: (builder) => ({
    getSupAdmin: builder.query<GeneralResponse<IAdmin[]>, { filter?: string }>({
      query: ({ filter }) => `/superadmin/workforce/admin${filter ?? ""}`,
      providesTags: ["admin"],
    }),
    getWsAdmin: builder.query<GeneralResponse<IAdmin[]>, { filter?: string }>({
      query: ({ filter }) =>
        `workspace/${getWorkspace()}/workforce/admin${filter ?? ""}`,
      providesTags: ["admin"],
    }),

    getSingleAdmin: builder.query<IAdmin, { userId: string }>({
      query: ({ userId }) => `/superadmin/workforce/admin/${userId}`,
      providesTags: ["single-admin"],
      transformResponse: (response: APIResponse<IAdmin>) => response?.data,
    }),

    toggleSupStatus: builder.mutation<
      IAdmin,
      { userId: string; status: AdminStatus }
    >({
      query: ({ userId, status }) => ({
        url: `/superadmin/workforce/admin/${userId}/${status}`,
        method: REQUEST_METHODS.POST,
      }),
      invalidatesTags: ["admin"],
    }),
    toggleWsStatus: builder.mutation<
      IAdmin,
      { userType: string; userId: string; status: string; data?: object }
    >({
      query: ({ userType, userId, status, data }) => ({
        url: `/workspace/${getWorkspace()}/workforce/${userType}/${userId}/${status}`,
        method: REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: ["admin"],
    }),

    alterAdmin: builder.mutation<object, { data?: object; id?: string }>({
      query: ({ data, id }) => ({
        url: `/superadmin/workforce/admin/${id ?? ""}`,
        method: id ? REQUEST_METHODS.PATCH : REQUEST_METHODS.POST,
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => {
        // Make sure these strings match exactly with your tag definitions
        const tags: ("admin" | "single-admin")[] = [];
        tags.push("admin");
        if (id) {
          tags.push("single-admin");
        }

        return tags;
      },
    }),
  }),
});

export const {
  useGetSupAdminQuery,
  useGetSingleAdminQuery,
  useGetWsAdminQuery,
  useToggleSupStatusMutation,
  useToggleWsStatusMutation,
  useAlterAdminMutation,
} = adminApi;
